import { Check, Loader2, Minus, Plus } from "lucide-react"
import * as React from "react"

import { useCtrlWheelZoom, useDragPan } from "@/lib/pan-zoom"
import { cn } from "@/lib/utils"

import type { FindingId, PointRect } from "../../data/review-data"
import type { ReportFindingItem } from "../report-data"
import { clamp, useElementSize, usePdfPage, type PageGeometry } from "../use-pdf-page"

import {
  ZOOM_CEIL,
  ZOOM_FLOOR,
  centreOn,
  panBy,
  zoomAround,
  zoomAroundCentre,
  type LabelView,
} from "./label-view"

/** A focused step frames its highlight at about this share of the viewport's width. */
const FOCUS_FRACTION = 0.34
const AUTO_ZOOM_MIN = 1.35
const AUTO_ZOOM_MAX = 2.8
const ZOOM_STEP = 0.25

interface Fraction {
  x: number
  y: number
  w: number
  h: number
}

function toFraction(rect: PointRect, geometry: PageGeometry): Fraction {
  return {
    x: rect.x0 / geometry.pointWidth,
    y: rect.y0 / geometry.pointHeight,
    w: (rect.x1 - rect.x0) / geometry.pointWidth,
    h: (rect.y1 - rect.y0) / geometry.pointHeight,
  }
}

interface Mark {
  key: string
  findingId: FindingId
  number: number
  fraction: Fraction
  /** The finding on the current step. */
  active: boolean
  /** The one occurrence of that finding the step is framed on. */
  current: boolean
  done: boolean
}

export interface FocusedLabelProps {
  /** The printed label. Empty when the review has none attached. */
  documentUrl: string
  findings: readonly ReportFindingItem[]
  activeId: FindingId | null
  /** Which of the active finding's located occurrences the step is framed on. */
  occurrenceIndex: number
  isDone: (id: FindingId) => boolean
}

/**
 * The printed label, zoomed and centred on the current step's highlight.
 *
 * The page renders once per step at the viewport's width and the whole stage
 * is translated and scaled by CSS, so moving between steps — or between the
 * occurrences of one — is an animation rather than a re-render.
 */
export function FocusedLabel({
  documentUrl,
  findings,
  activeId,
  occurrenceIndex,
  isDone,
}: FocusedLabelProps): JSX.Element {
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const { width: vpW, height: vpH } = useElementSize(viewportRef)

  const active = findings.find((f) => f.id === activeId) ?? null
  const focusRect = active
    ? (active.locations[occurrenceIndex] ?? active.locations[0] ?? null)
    : null
  const page = focusRect?.page ?? 1

  const { geometry, loading, failed } = usePdfPage(documentUrl, page, vpW, canvasRef)

  // Null follows the step: the label frames the current finding. Zooming or
  // dragging takes it over until the next step, or until the reader resets.
  const [manual, setManual] = React.useState<LabelView | null>(null)
  React.useEffect(() => {
    setManual(null)
  }, [activeId, occurrenceIndex])

  /*
     Every mark on the rendered page. Other findings show their first mark on
     the page, dimmed, so the client can see what else is nearby. The active
     finding shows each of its occurrences on the page, with the one the step
     is framed on picked out.
  */
  const marks = React.useMemo<Mark[]>(() => {
    if (!geometry) return []
    const out: Mark[] = []
    for (const f of findings) {
      const done = isDone(f.id)
      if (f.id === activeId) {
        f.locations.forEach((rect, i) => {
          if (rect.page !== geometry.page) return
          out.push({
            key: `${f.id}:${String(i)}`,
            findingId: f.id,
            number: f.number,
            fraction: toFraction(rect, geometry),
            active: true,
            current: i === occurrenceIndex,
            done,
          })
        })
        continue
      }
      const first = f.locations.find((rect) => rect.page === geometry.page)
      if (!first) continue
      out.push({
        key: f.id,
        findingId: f.id,
        number: f.number,
        fraction: toFraction(first, geometry),
        active: false,
        current: false,
        done,
      })
    }
    return out
  }, [findings, activeId, occurrenceIndex, geometry, isDone])

  const region =
    geometry && focusRect?.page === geometry.page ? toFraction(focusRect, geometry) : null
  const stageW = geometry?.width ?? vpW
  const stageH = geometry?.height ?? 0

  const sizes = { vpW, vpH, stageW, stageH }
  const autoZoom =
    region && region.w > 0 ? clamp(FOCUS_FRACTION / region.w, AUTO_ZOOM_MIN, AUTO_ZOOM_MAX) : 1
  const view = manual ?? centreOn(region, autoZoom, sizes)
  const { zoom, tx, ty } = view
  // Only a page larger than its viewport, in either direction, has anywhere to go.
  const canPan = stageW * zoom > vpW + 0.5 || stageH * zoom > vpH + 0.5

  // Drag to pan, gliding on when let go. `from` is the view at the press, which every move is
  // measured from; `live` is the view as last set, which each glide frame steps on from.
  const fromRef = React.useRef<LabelView>(view)
  const liveRef = React.useRef<LabelView>(view)
  const pan = useDragPan({
    onPress: () => {
      if (!canPan) return false
      fromRef.current = view
      liveRef.current = view
      return true
    },
    onMove: (dx, dy) => {
      liveRef.current = panBy(fromRef.current, dx, dy, sizes)
      setManual(liveRef.current)
    },
    onGlide: (vx, vy) => {
      const current = liveRef.current
      const next = panBy(current, vx, vy, sizes)
      liveRef.current = next
      setManual(next)
      return { x: next.tx !== current.tx, y: next.ty !== current.ty }
    },
  })
  const { dragging, gliding, stopGlide } = pan
  // A new step reframes; a glide left over from the last one must not carry on past it.
  React.useEffect(() => {
    stopGlide()
  }, [activeId, occurrenceIndex, stopGlide])

  const adjust = (delta: number): void => {
    stopGlide()
    setManual(zoomAroundCentre(view, zoom + delta, sizes))
  }

  // Ctrl + wheel zooms on the pointer, as in a PDF viewer.
  const wheeling = useCtrlWheelZoom(viewportRef, (factor, x, y) => {
    if (stageH <= 0) return
    stopGlide()
    setManual((current) =>
      zoomAround(current ?? view, (current ?? view).zoom * factor, x, y, sizes),
    )
  })

  return (
    <div
      ref={viewportRef}
      {...pan.handlers}
      className={cn(
        "relative h-[60vh] min-h-[420px] w-full touch-none select-none overflow-hidden rounded-lg border border-border bg-surface-sunken lg:h-[74vh] lg:min-h-0 lg:flex-1",
        canPan && (dragging ? "cursor-grabbing" : "cursor-grab"),
      )}
    >
      <div
        className={cn(
          "absolute left-0 top-0 origin-top-left",
          // A drag, a glide or a turning wheel follows the pointer; only a reframe animates.
          !dragging &&
            !gliding &&
            !wheeling &&
            "transition-transform duration-400 ease-out motion-reduce:transition-none",
        )}
        style={{
          width: stageW || "100%",
          height: stageH || "auto",
          transform: `translate(${String(tx)}px, ${String(ty)}px) scale(${String(zoom)})`,
        }}
      >
        <canvas
          ref={canvasRef}
          aria-label="Printed label"
          role="img"
          className={cn("block bg-white", (failed || !documentUrl) && "hidden")}
          style={{ width: stageW || undefined, height: stageH || undefined }}
        />
        {marks.map((m) => (
          <LabelMark key={m.key} mark={m} zoom={zoom} />
        ))}
      </div>

      {!documentUrl && (
        <Notice>
          The printed label is not attached to this review, so the location cannot be shown. The
          divergence itself is described in full beside it.
        </Notice>
      )}
      {documentUrl && loading && !geometry && (
        <div className="absolute inset-0 grid place-items-center">
          <Loader2 className="size-5 animate-spin text-text-3 motion-reduce:animate-none" />
          <span className="sr-only">Loading the printed label</span>
        </div>
      )}
      {/* A written sentence, not the renderer's: this is a client deliverable. */}
      {failed && (
        <Notice>
          The printed label could not be displayed. The divergence itself is described in full
          beside it.
        </Notice>
      )}
      {documentUrl && geometry && !region && (
        <div className="absolute inset-x-0 bottom-11 mx-auto w-fit rounded-full border border-border bg-surface/90 px-3 py-1 text-caption text-text-3 shadow-e1 backdrop-blur">
          This divergence has no verified location on the label
        </div>
      )}

      <div
        // Its own presses are never the start of a pan.
        onPointerDown={(event) => {
          event.stopPropagation()
        }}
        title="Ctrl + scroll to zoom, drag to move around"
        className="absolute bottom-2 right-3 flex items-center gap-0.5 rounded-full border border-border bg-surface/90 px-1 py-0.5 text-caption font-medium text-text-2 shadow-e1 backdrop-blur"
      >
        <button
          type="button"
          onClick={() => {
            adjust(-ZOOM_STEP)
          }}
          disabled={zoom <= ZOOM_FLOOR + 0.001}
          aria-label="Zoom out"
          className="grid size-6 place-items-center rounded-full hover:bg-surface-sunken disabled:opacity-40"
        >
          <Minus className="size-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => {
            stopGlide()
            setManual(null)
          }}
          aria-label="Reset zoom to frame this finding"
          className="min-w-12 rounded px-1 text-center tabular-nums hover:text-text"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          onClick={() => {
            adjust(ZOOM_STEP)
          }}
          disabled={zoom >= ZOOM_CEIL - 0.001}
          aria-label="Zoom in"
          className="grid size-6 place-items-center rounded-full hover:bg-surface-sunken disabled:opacity-40"
        >
          <Plus className="size-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

/*
   Fixed colours, never theme tokens: the marks sit on the always-white page,
   where a token that flips in dark mode would vanish. The accent is the
   brand's light-theme value.
*/
const ACCENT = "#c8461a"
const ACCENT_FILL = "rgba(200, 70, 26, 0.22)"
const ACCENT_GLOW = "rgba(200, 70, 26, 0.45)"
const DONE = "#1d6b49"
const IDLE = "#8d96a3"

function LabelMark({ mark, zoom }: { mark: Mark; zoom: number }): JSX.Element {
  const { fraction: r, active, current, done } = mark
  // Counter-scaled so the ring and badge keep one on-screen size at any zoom.
  const ring = (current ? 3 : active ? 2 : 1) / zoom
  return (
    <div
      aria-hidden="true"
      className="absolute rounded-[2px] transition-all duration-260"
      style={{
        left: `${String(r.x * 100)}%`,
        top: `${String(r.y * 100)}%`,
        width: `${String(r.w * 100)}%`,
        height: `${String(r.h * 100)}%`,
        backgroundColor: current
          ? ACCENT_FILL
          : active
            ? "rgba(200, 70, 26, 0.1)"
            : "rgba(100,116,139,0.05)",
        boxShadow: current
          ? `0 0 0 ${String(ring)}px ${ACCENT}, 0 0 ${String(12 / zoom)}px ${String(4 / zoom)}px ${ACCENT_GLOW}`
          : `0 0 0 ${String(ring)}px ${active ? ACCENT : "rgba(100,116,139,0.45)"}`,
        opacity: active ? 1 : 0.45,
      }}
    >
      {(!active || current) && (
        <span
          className="absolute -left-1 -top-1 grid origin-bottom-right place-items-center rounded-full text-[10px] font-bold text-white shadow"
          style={{
            height: 18,
            width: 18,
            transform: `scale(${String(1 / zoom)})`,
            backgroundColor: done ? DONE : current ? ACCENT : IDLE,
          }}
        >
          {done ? <Check className="size-3" strokeWidth={3} /> : mark.number}
        </span>
      )}
    </div>
  )
}

function Notice({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="absolute inset-0 grid place-items-center px-8 text-center text-body-sm text-text-3">
      <p className="max-w-[46ch]">{children}</p>
    </div>
  )
}
